import type { MappableField } from './pdfFieldMapping';

/**
 * Turns the flat list of mappable fields into the step → section → row outline
 * the mapping page's left panel renders.
 *
 * The panel used to be one flat list — 523 rows on the RDSP form, each printing
 * its whole `Step › Section › Field` breadcrumb and so running two or three
 * lines tall. Finding the row that matches the part of the PDF you are looking
 * at meant scrolling past everything else. Steps already run in the same order
 * as the printed form, so grouping by step and then section turns that scroll
 * into two clicks, and lets each row print only its own name.
 *
 * Pure so the ordering and counting rules are testable without mounting the
 * page, which is the same reason `resolveDrop` and `deriveCards` live apart
 * from their components.
 */

export type OutlineRow = {
  kind: 'row';
  field: MappableField;
};

/** A repeatable field's rows, kept collapsible inside their section. */
export type OutlineGroup = {
  kind: 'group';
  key: string;
  label: string;
  fields: MappableField[];
  mapped: number;
};

export type OutlineSection = {
  sectionId: string;
  label: string;
  entries: (OutlineRow | OutlineGroup)[];
  /** Every field under this section, groups flattened — drives the counts. */
  fields: MappableField[];
  mapped: number;
};

export type OutlineStep = {
  stepId: string;
  label: string;
  sections: OutlineSection[];
  fields: MappableField[];
  mapped: number;
};

/**
 * Build the outline.
 *
 * `isMapped` is injected rather than read off the field because "mapped" is
 * relative to the PDF currently selected in the viewer — the same field can be
 * mapped in one document and not another.
 */
export function buildOutline(
  fields: MappableField[],
  isMapped: (f: MappableField) => boolean,
): OutlineStep[] {
  const steps: OutlineStep[] = [];
  const stepAt = new Map<string, number>();
  const sectionAt = new Map<string, number>();
  const groupAt = new Map<string, OutlineGroup>();

  for (const f of fields) {
    // Insertion order is form order, which is what makes this navigable — never
    // sort these.
    let si = stepAt.get(f.stepId);
    if (si === undefined) {
      si = steps.length;
      stepAt.set(f.stepId, si);
      steps.push({ stepId: f.stepId, label: f.stepLabel, sections: [], fields: [], mapped: 0 });
    }
    const step = steps[si]!;

    const sectionKey = `${f.stepId}:${f.sectionId}`;
    let ci = sectionAt.get(sectionKey);
    if (ci === undefined) {
      ci = step.sections.length;
      sectionAt.set(sectionKey, ci);
      step.sections.push({
        sectionId: f.sectionId,
        label: f.sectionLabel,
        entries: [],
        fields: [],
        mapped: 0,
      });
    }
    const section = step.sections[ci]!;

    if (f.groupKey === undefined) {
      section.entries.push({ kind: 'row', field: f });
    } else {
      const gk = `${sectionKey}:${f.groupKey}`;
      let group = groupAt.get(gk);
      if (!group) {
        group = {
          kind: 'group',
          key: f.groupKey,
          // The group header already sits under its step and section, so strip
          // that prefix rather than printing it a third time. A REPEATABLE
          // SECTION has no field part to strip down to — its group spans the
          // whole section — so it falls back to the section's own name and the
          // renderer drops the now-redundant sub-header above it.
          label:
            stripPrefix(f.groupLabel ?? f.leafLabel, `${f.stepLabel} › ${f.sectionLabel} › `) ===
            (f.groupLabel ?? f.leafLabel)
              ? f.sectionLabel
              : stripPrefix(f.groupLabel ?? f.leafLabel, `${f.stepLabel} › ${f.sectionLabel} › `),
          fields: [],
          mapped: 0,
        };
        groupAt.set(gk, group);
        section.entries.push(group);
      }
      group.fields.push(f);
      if (isMapped(f)) group.mapped += 1;
    }

    section.fields.push(f);
    step.fields.push(f);
    if (isMapped(f)) {
      section.mapped += 1;
      step.mapped += 1;
    }
  }
  return steps;
}

function stripPrefix(value: string, prefix: string): string {
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

/**
 * Narrow the list before it is grouped.
 *
 * `query` matches the row's own name AND its breadcrumb, so searching a step
 * name keeps that whole step — the natural thing to try when you know roughly
 * where you are in the PDF.
 */
export function filterFields(
  fields: MappableField[],
  query: string,
  unmappedOnly: boolean,
  isMapped: (f: MappableField) => boolean,
): MappableField[] {
  const needle = query.trim().toLowerCase();
  return fields.filter((f) => {
    if (unmappedOnly && isMapped(f)) return false;
    if (!needle) return true;
    return f.label.toLowerCase().includes(needle) || f.leafLabel.toLowerCase().includes(needle);
  });
}

/**
 * Which step to open when the page loads: the first with anything left to map,
 * so the panel opens on the work rather than on a wall of finished steps. Falls
 * back to the first step when everything is mapped.
 */
export function initialExpandedStep(steps: OutlineStep[]): string | null {
  const pending = steps.find((s) => s.mapped < s.fields.length);
  return (pending ?? steps[0])?.stepId ?? null;
}
